import {
  TIMELINE_MODERNIZED_LINK_COLOR,
  TIMELINES_ITEM_ROW_HEIGHT,
  EXPANDER_BUTTON_SIZE,
  TIMELINE_MODERNIZED_GROUP_INDENTATION,
  TIMELINE_MODERNIZED_COMPONENT_INDENTATION,
  MARGIN_BETWEEN_ROWS,
} from '../consts';
import styled from 'styled-components';
import { radius } from '@ardoq/design-tokens';
import { useMemo } from 'react';
import { getModernizedItemTreeIndentation } from '../calculations';

const LINK_THICKNESS = 1;
const EXPANDER_OFFSET = EXPANDER_BUTTON_SIZE / 2;
const ROW_HEIGHT_ADJUSTMENT = TIMELINES_ITEM_ROW_HEIGHT / 2;
const LINK_BORDER_STYLE = `${LINK_THICKNESS}px dashed ${TIMELINE_MODERNIZED_LINK_COLOR}`;

const LabelsColumnListLinkContainer = styled.div`
  position: relative;
  pointer-events: none;
`;

const LabelsColumnListLinkBase = styled.div`
  position: absolute;
  z-index: 2;
`;

const LabelsColumnListLeafLink = styled(LabelsColumnListLinkBase)`
  height: ${ROW_HEIGHT_ADJUSTMENT}px;
  right: 0;
  &:before {
    content: '';
    display: block;
    position: absolute;
    width: 100%;
    height: 50%;
    bottom: 0;
    left: 0;
    border-bottom: ${LINK_BORDER_STYLE};
  }
`;

const LabelsColumnListLinkStem = styled(LabelsColumnListLinkBase)`
  top: ${ROW_HEIGHT_ADJUSTMENT + EXPANDER_OFFSET}px;
  border-left: ${LINK_BORDER_STYLE};
  border-bottom: ${LINK_BORDER_STYLE};
  border-radius: 0 0 0 ${radius.r8};
`;

type LinkStemStyle = {
  /**
   * A "transit link" is a line connecting a parent to its child that passes through the
   * current row (parent is above the current row, its child is below the current row).
   */
  transitLinks: number[];
  treeDepth: number;
  isGroup?: boolean;
  isLastChild?: boolean;
  rowHeight: number;
  isComponentHierarchyCollapsed: boolean;
};

const getLastChildLinkStemStyle = (left: number, isGroup?: boolean) => ({
  top: 0,
  height: ROW_HEIGHT_ADJUSTMENT,
  width:
    (isGroup
      ? TIMELINE_MODERNIZED_GROUP_INDENTATION
      : TIMELINE_MODERNIZED_COMPONENT_INDENTATION) -
    EXPANDER_OFFSET +
    LINK_THICKNESS,
  left,
});

const getTransitLinkStemStyle = (rowHeight: number, left: number) => ({
  top: 0,
  height: rowHeight + MARGIN_BETWEEN_ROWS - 1, // -1 to avoid overlap with the next row's stem
  width: LINK_THICKNESS,
  left,
});

const getIndent = (treeDepth: number) =>
  Math.max(0, treeDepth - 1) * TIMELINE_MODERNIZED_GROUP_INDENTATION +
  EXPANDER_OFFSET;

const getLinkStyles = ({
  transitLinks,
  isLastChild,
  isGroup,
  treeDepth,
  rowHeight,
  isComponentHierarchyCollapsed,
}: LinkStemStyle) => {
  const linkStemIndent = getIndent(treeDepth);

  const linkStemStyle =
    treeDepth > 0
      ? isLastChild
        ? getLastChildLinkStemStyle(linkStemIndent, isGroup)
        : getTransitLinkStemStyle(rowHeight, linkStemIndent)
      : null;

  const linkLeafStyle =
    isLastChild || treeDepth === 0
      ? null
      : {
          width: `${
            (isGroup
              ? TIMELINE_MODERNIZED_GROUP_INDENTATION
              : TIMELINE_MODERNIZED_COMPONENT_INDENTATION) - EXPANDER_OFFSET
          }px`,
        };

  const transitLinkStyles = transitLinks.map(linkDepth =>
    getTransitLinkStemStyle(rowHeight, getIndent(linkDepth))
  );

  const expanderLinkStyle =
    isGroup && !isComponentHierarchyCollapsed
      ? {
          top: ROW_HEIGHT_ADJUSTMENT + EXPANDER_OFFSET + 4,
          height: rowHeight - EXPANDER_OFFSET - ROW_HEIGHT_ADJUSTMENT - 1, // -1 to avoid overlap with the next row's stem
          width: LINK_THICKNESS,
          left: getIndent(treeDepth + 1),
        }
      : null;

  return { linkStemStyle, linkLeafStyle, transitLinkStyles, expanderLinkStyle };
};

const Link = ({
  transitLinks,
  isGroup,
  isLastChild,
  treeDepth,
  rowHeight,
  isComponentHierarchyCollapsed,
}: CollapsibleLinkArgs) => {
  /**
   * linkStem is the vertical line spanning between a parent and its children.
   * linkLeaf is the horizontal line connecting an item to linkStem.
   *
   * A "transit link" is a line connecting a parent to its child that passes through the
   * current row (parent is above the current row, its child is below the current row).
   */

  const { linkStemStyle, linkLeafStyle, transitLinkStyles, expanderLinkStyle } =
    useMemo(
      () =>
        getLinkStyles({
          transitLinks,
          isLastChild,
          treeDepth,
          isGroup,
          rowHeight,
          isComponentHierarchyCollapsed,
        }),

      [
        transitLinks,
        isLastChild,
        treeDepth,
        isGroup,
        rowHeight,
        isComponentHierarchyCollapsed,
      ]
    );

  return (
    <>
      {linkStemStyle ? (
        <LabelsColumnListLinkStem style={linkStemStyle} />
      ) : null}

      {linkLeafStyle ? (
        <LabelsColumnListLeafLink style={linkLeafStyle} />
      ) : null}

      {transitLinkStyles.map((style, index) => (
        <LabelsColumnListLinkStem key={index} style={style} />
      ))}

      {expanderLinkStyle ? (
        <LabelsColumnListLinkStem style={expanderLinkStyle} />
      ) : null}
    </>
  );
};

export type CollapsibleLinkArgs = {
  isGroup?: boolean;
  treeDepth: number;
  isLastChild: boolean;
  transitLinks: number[];
  rowHeight: number;
  isComponentHierarchyCollapsed: boolean;
};
const LabelsColumnListLinkModernized = (props: CollapsibleLinkArgs) => {
  const { treeDepth, isGroup } = props;

  const indentation = getModernizedItemTreeIndentation(treeDepth, isGroup);

  return (
    <LabelsColumnListLinkContainer
      style={
        treeDepth
          ? { width: `${indentation}px`, minWidth: `${indentation}px` }
          : undefined
      }
    >
      <Link {...props} />
    </LabelsColumnListLinkContainer>
  );
};
export { LabelsColumnListLinkModernized };
